import { applyMiddleware, compose, createStore } from 'redux';
import thunk from 'redux-thunk';

import { StoreState } from '../interfaces';
import { AppState, rootReducer } from '../reducers';
import { initialHomographyState } from '../reducers/homography/homographyState';
import { initialStoreState } from '../reducers/store/storeState';
import { deepCopy, isObject } from '../utils';

declare let module: any;
declare let window: any;
declare let localStorage: any;

const localStorageKey = 'newAppState';

class Store {
  private composeFn = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
  private enhancer = this.composeFn(applyMiddleware(thunk));
  private store: any;

  loadStateFromLocalStorage<T>(lsKey: string, defaultState: T): T {
    const localStorageState = localStorage.getItem(lsKey);
    if (localStorageState) {
      return sanitizePreloadedState(JSON.parse(localStorageState), defaultState);
    }
    return defaultState;
  }

  updateStateInLocalStorage<T>(lsKey: string, _store: T, fieldsToInclude?: (keyof T)[]): void {
    let store: T = {} as T;
    if (fieldsToInclude) {
      fieldsToInclude.forEach((key) => {
        store[key] = _store[key];
      });
    } else {
      store = _store;
    }
    localStorage.setItem(
      lsKey,
      JSON.stringify({
        ...store,
      }),
    );
  }

  configureStore() {
    const state: AppState = {
      store: this.loadStateFromLocalStorage<StoreState>(localStorageKey, initialStoreState),
      homography: initialHomographyState,
    };

    this.store = createStore(rootReducer, state, this.enhancer);

    if (module.hot) {
      module.hot.accept('../reducers', () => {
        const nextRootReducer = require('../reducers').default;
        this.store.replaceReducer(nextRootReducer);
      });
    }

    // Expose the data if we're in testing
    if (window.Cypress) {
      window.__store__ = this.store;
    }

    return this.getStore();
  }

  getStore() {
    return this.store;
  }

  clearStore() {
    localStorage.removeItem(localStorageKey);
  }
}

const sanitizePreloadedState = <T>(unfilteredState: T, defaultState: T) => {
  const state = deepCopy(unfilteredState);
  const keys = Object.keys(defaultState) as (keyof typeof defaultState)[];

  keys.forEach((key) => {
    // all fields in default state if different type should be set to defaults
    if (typeof state[key] !== typeof defaultState[key]) {
      let initialValue = defaultState[key]; // set default for types that were changed
      if (isObject(initialValue)) {
        // prevent passing a reference
        initialValue = deepCopy(defaultState[key]);
      }
      state[key] = initialValue;
    } else if (isObject(state[key]) && isObject(defaultState[key])) {
      state[key] = sanitizePreloadedState(state[key], defaultState[key]);
    }
  });
  return state;
};

export const store = new Store().configureStore();
export default store;
